import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Users,
  Edit2,
  Trash2,
  Mail,
  Phone,
  RefreshCw,
  ExternalLink,
  Merge,
  Check,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const CONTACT_TYPES = [
  { value: "buyer", label: "Buyer" },
  { value: "seller", label: "Seller" },
  { value: "buyer_agent", label: "Buyer Agent" },
  { value: "seller_agent", label: "Seller Agent" },
  { value: "lender", label: "Lender" },
  { value: "title_company", label: "Title Company" },
  { value: "inspector", label: "Inspector" },
  { value: "appraiser", label: "Appraiser" },
  { value: "contractor", label: "Contractor" },
  { value: "other", label: "Other" }
];

const Contacts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [primaryId, setPrimaryId] = useState(null);
  const [merging, setMerging] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    contact_type: "buyer",
    notes: ""
  });

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts`, { withCredentials: true });
      setContacts(response.data);
    } catch (error) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
    if (searchParams.get("action") === "new") {
      setIsModalOpen(true);
      searchParams.delete("action");
      setSearchParams(searchParams);
    }
  }, [fetchContacts, searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.contact_id}`, formData, { withCredentials: true });
        toast.success("Contact updated");
      } else {
        await axios.post(`${API}/contacts`, formData, { withCredentials: true });
        toast.success("Contact created");
      }
      setIsModalOpen(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contact");
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email || "",
      phone: contact.phone || "",
      contact_type: contact.contact_type,
      notes: contact.notes || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;
    try {
      await axios.delete(`${API}/contacts/${contactToDelete.contact_id}`, { withCredentials: true });
      toast.success("Contact deleted");
      setShowDeleteDialog(false);
      setContactToDelete(null);
      fetchContacts();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const resetForm = () => {
    setEditingContact(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      contact_type: "buyer",
      notes: ""
    });
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredContacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredContacts.map(c => c.contact_id)));
    }
  };

  const openMergeDialog = () => {
    if (selected.size < 2) return;
    setPrimaryId([...selected][0]);
    setShowMergeDialog(true);
  };

  const handleMerge = async () => {
    if (!primaryId || selected.size < 2) return;
    setMerging(true);
    try {
      const mergeIds = [...selected].filter(id => id !== primaryId);
      const res = await axios.post(`${API}/contacts/merge`, {
        primary_id: primaryId,
        merge_ids: mergeIds,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setShowMergeDialog(false);
      setSelected(new Set());
      setPrimaryId(null);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone?.includes(searchTerm);
    const matchesType = typeFilter === "all" || contact.contact_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getContactTypeLabel = (type) => {
    return CONTACT_TYPES.find(t => t.value === type)?.label || type;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)] tracking-tight">
              Contacts
            </h1>
            <p className="text-[var(--t-text-muted)] mt-1">Manage clients, agents, and service providers</p>
          </div>
          <div className="flex gap-2">
            {selected.size >= 2 && (
              <Button
                variant="outline"
                onClick={openMergeDialog}
                className="text-[var(--t-primary)] border-[var(--t-primary)]/30 hover:bg-[var(--t-primary)]/5"
                data-testid="merge-contacts-btn"
              >
                <Merge className="w-4 h-4 mr-2" />
                Merge ({selected.size})
              </Button>
            )}
            {selected.size > 0 && selected.size < 2 && (
              <span className="inline-flex items-center text-xs text-[var(--t-text-muted)] px-3">
                Select 1 more to merge
              </span>
            )}
            <Button
              onClick={() => { resetForm(); setIsModalOpen(true); }}
              className="btn-primary"
              data-testid="new-contact-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Contact
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-subtle)]" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-contacts"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="type-filter">
            <Users className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {CONTACT_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Contacts Table */}
      {filteredContacts.length > 0 ? (
        <Card className="card-surface" data-testid="contacts-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="table-header w-10">
                  <button
                    onClick={toggleAll}
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selected.size === filteredContacts.length && filteredContacts.length > 0 ? 'bg-[var(--t-primary)] border-[var(--t-primary)] text-white' : 'border-[var(--t-border)] hover:border-[var(--t-primary)]'}`}
                    data-testid="select-all-contacts"
                  >
                    {selected.size === filteredContacts.length && filteredContacts.length > 0 && <Check className="w-3 h-3" />}
                  </button>
                </TableHead>
                <TableHead className="table-header">Name</TableHead>
                <TableHead className="table-header">Type</TableHead>
                <TableHead className="table-header">Email</TableHead>
                <TableHead className="table-header">Phone</TableHead>
                <TableHead className="table-header">Transactions</TableHead>
                <TableHead className="table-header text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContacts.map((contact, index) => {
                const rowIdx = index % 8 + 1;
                return (
                <TableRow key={contact.contact_id} style={{ background: `var(--t-row-${rowIdx})` }} data-testid={`contact-row-${index}`}>
                  <TableCell className="table-cell w-10">
                    <button
                      onClick={() => toggleSelect(contact.contact_id)}
                      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selected.has(contact.contact_id) ? 'bg-[var(--t-primary)] border-[var(--t-primary)] text-white' : 'border-[var(--t-border)] hover:border-[var(--t-primary)]'}`}
                      data-testid={`select-contact-${index}`}
                    >
                      {selected.has(contact.contact_id) && <Check className="w-3 h-3" />}
                    </button>
                  </TableCell>
                  <TableCell className="table-cell font-medium">{contact.name}</TableCell>
                  <TableCell className="table-cell">
                    <Badge variant="outline" className="capitalize text-xs">
                      {getContactTypeLabel(contact.contact_type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="table-cell">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className="text-[var(--t-primary)] hover:underline flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {contact.email}
                      </a>
                    ) : (
                      <span className="text-[var(--t-text-subtle)]">-</span>
                    )}
                  </TableCell>
                  <TableCell className="table-cell">
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`} className="text-[var(--t-text)] flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {contact.phone}
                      </a>
                    ) : (
                      <span className="text-[var(--t-text-subtle)]">-</span>
                    )}
                  </TableCell>
                  <TableCell className="table-cell" data-testid={`contact-transactions-${index}`}>
                    {contact.linked_transactions?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {contact.linked_transactions.map((txn) => (
                          <Link
                            key={txn.transaction_id}
                            to={`/transactions/${txn.transaction_id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors hover:ring-1 hover:ring-[var(--t-primary)]/40"
                            style={{
                              background: txn.status === 'closed' ? '#dcfce7' : txn.status === 'cancelled' ? '#fef2f2' : '#eff6ff',
                              color: txn.status === 'closed' ? '#166534' : txn.status === 'cancelled' ? '#991b1b' : '#1e40af',
                            }}
                            title={`${txn.property_address} — ${txn.role}`}
                            data-testid={`txn-link-${txn.transaction_id}`}
                          >
                            <ExternalLink className="w-2.5 h-2.5" />
                            <span className="max-w-[140px] truncate">{txn.property_address}</span>
                            <span className="opacity-60">({txn.role})</span>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[var(--t-text-subtle)] text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="table-cell text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(contact)}
                        data-testid={`edit-contact-${index}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => { setContactToDelete(contact); setShowDeleteDialog(true); }}
                        data-testid={`delete-contact-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="card-surface">
          <CardContent className="py-12">
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <p className="empty-state-title">No contacts found</p>
              <p className="empty-state-description">
                {searchTerm || typeFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "Get started by adding your first contact"}
              </p>
              {!searchTerm && typeFilter === "all" && (
                <Button 
                  onClick={() => { resetForm(); setIsModalOpen(true); }}
                  className="btn-primary mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Contact
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent data-testid="contact-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold [font-family:var(--t-heading-font)]">
              {editingContact ? 'Edit Contact' : 'New Contact'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="input-contact-name"
              />
            </div>
            <div className="form-group">
              <Label>Contact Type *</Label>
              <Select 
                value={formData.contact_type} 
                onValueChange={(v) => setFormData({ ...formData, contact_type: v })}
              >
                <SelectTrigger data-testid="select-contact-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="input-contact-email"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="input-contact-phone"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="input-contact-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-contact">
                {editingContact ? 'Update Contact' : 'Create Contact'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-contact-dialog">
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{contactToDelete?.name}&rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-contact">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-lg" data-testid="merge-contacts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-[var(--t-primary)]" />
              Merge Contacts
            </DialogTitle>
            <DialogDescription>
              Choose the primary contact to keep. The others will be deleted and all their transaction references will be updated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 my-2 max-h-[300px] overflow-y-auto">
            {contacts.filter(c => selected.has(c.contact_id)).map((c) => {
              const isPrimary = c.contact_id === primaryId;
              const txnCount = c.linked_transactions?.length || 0;
              return (
                <button
                  key={c.contact_id}
                  onClick={() => setPrimaryId(c.contact_id)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${isPrimary ? 'border-[var(--t-primary)] bg-[var(--t-primary)]/5 ring-1 ring-[var(--t-primary)]/20' : 'border-[var(--t-border)] hover:border-[var(--t-primary)]/40'}`}
                  data-testid={`merge-pick-${c.contact_id}`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${isPrimary ? 'border-[var(--t-primary)] bg-[var(--t-primary)]' : 'border-[var(--t-border)]'}`}>
                    {isPrimary && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[var(--t-text)]">{c.name}</p>
                    <p className="text-xs text-[var(--t-text-muted)] truncate">{c.email || "No email"}{c.phone ? ` · ${c.phone}` : ""}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px] capitalize">{getContactTypeLabel(c.contact_type)}</Badge>
                      {txnCount > 0 && (
                        <span className="text-[10px] text-[var(--t-text-subtle)]">{txnCount} linked txn{txnCount > 1 ? "s" : ""}</span>
                      )}
                      {isPrimary && (
                        <Badge className="text-[10px] bg-[var(--t-primary)] text-white">Primary — Keep</Badge>
                      )}
                      {!isPrimary && (
                        <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">Will be deleted</Badge>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="text-xs p-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800" data-testid="merge-summary">
            <strong>{selected.size - 1}</strong> contact{selected.size - 1 > 1 ? "s" : ""} will be deleted. All their transaction references will point to <strong>{contacts.find(c => c.contact_id === primaryId)?.name || "the primary"}</strong>.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleMerge}
              disabled={merging || !primaryId}
              className="bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]"
              data-testid="confirm-merge-btn"
            >
              {merging ? "Merging..." : `Merge ${selected.size} Contacts`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
